import { useEffect, useState } from 'react';
import { Building2, HelpCircle, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, recordFunnelEvent } from '../api.js';
import { AppShell } from '../components/AppShell.js';
import { Button, Field, Input, Notice } from '../components/ui.js';
import { copy } from '../i18n.js';
import { useDraft } from '../state/DraftContext.js';

interface PlaceSuggestion {
  placeId: string;
  label: string;
}
interface ResolvedPlace {
  placeId: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export function PlacePage() {
  const { draft, update } = useDraft();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'VENUE' | 'DIFFERENT' | 'UNKNOWN'>(
    draft?.place?.kind === 'UNKNOWN'
      ? 'UNKNOWN'
      : draft?.place?.kind === 'VENUE'
        ? 'VENUE'
        : 'DIFFERENT',
  );
  const [query, setQuery] = useState(
    draft?.place && draft.place.kind !== 'UNKNOWN' && draft.place.kind !== 'VENUE'
      ? draft.place.name
      : '',
  );
  const [context, setContext] = useState(
    draft?.place && draft.place.kind !== 'UNKNOWN' ? (draft.place.context ?? '') : '',
  );
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  if (!draft) return null;
  const t = copy[draft.language];

  useEffect(() => {
    if (mode !== 'DIFFERENT' || query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setSearchError(false);
      api<PlaceSuggestion[]>(`/api/places/autocomplete?q=${encodeURIComponent(query.trim())}`, {
        signal: controller.signal,
      })
        .then(setSuggestions)
        .catch((error) => {
          if ((error as Error).name !== 'AbortError') setSearchError(true);
        })
        .finally(() => setLoading(false));
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mode, query]);

  const chooseMode = (next: typeof mode) => {
    setMode(next);
    if (next === 'VENUE')
      update({
        place: {
          kind: 'VENUE',
          venueId: draft.venue.id,
          name: draft.venue.name,
          ...(context ? { context } : {}),
          differentPlace: false,
        },
      });
    if (next === 'UNKNOWN') update({ place: { kind: 'UNKNOWN' } });
    if (next === 'DIFFERENT' && draft.place?.kind === 'VENUE') update({ place: null });
  };

  const chooseSuggestion = async (suggestion: PlaceSuggestion) => {
    setLoading(true);
    try {
      const place = await api<ResolvedPlace>(
        `/api/places/${encodeURIComponent(suggestion.placeId)}`,
      );
      setQuery(place.name);
      setSuggestions([]);
      update({
        place: { kind: 'GOOGLE', ...place, ...(context ? { context } : {}), differentPlace: true },
      });
    } catch {
      setSearchError(true);
    } finally {
      setLoading(false);
    }
  };

  const continueFlow = () => {
    if (mode === 'DIFFERENT' && (!draft.place || draft.place.kind === 'VENUE')) {
      if (!query.trim()) return;
      update({
        place: {
          kind: 'FREE_FORM',
          name: query.trim(),
          ...(context ? { context } : {}),
          differentPlace: true,
        },
      });
    } else if (draft.place && draft.place.kind !== 'UNKNOWN') {
      update({ place: { ...draft.place, ...(context ? { context } : {}) } });
    }
    recordFunnelEvent({ type: 'STEP_VIEWED', step: 'PLACE', language: draft.language });
    navigate('/report/proof');
  };

  const canContinue =
    mode !== 'DIFFERENT' ||
    Boolean(query.trim() || (draft.place && !['VENUE', 'UNKNOWN'].includes(draft.place.kind)));
  return (
    <AppShell language={draft.language} step={2}>
      <header className="page-heading">
        <h1>{t.place.title}</h1>
        <p>{t.place.lead}</p>
      </header>
      <section className="choice-list">
        <button
          className={`choice-card ${mode === 'VENUE' ? 'selected' : ''}`}
          type="button"
          onClick={() => chooseMode('VENUE')}
        >
          <Building2 />
          <span>
            <strong>{t.place.venue}</strong>
            <small>
              {draft.venue.name}, {draft.venue.city}
            </small>
          </span>
          <span className="radio-dot" />
        </button>
        <button
          className={`choice-card ${mode === 'DIFFERENT' ? 'selected' : ''}`}
          type="button"
          onClick={() => chooseMode('DIFFERENT')}
        >
          <MapPin />
          <span>
            <strong>{t.place.different}</strong>
            <small>{t.place.search}</small>
          </span>
          <span className="radio-dot" />
        </button>
        <button
          className={`choice-card ${mode === 'UNKNOWN' ? 'selected' : ''}`}
          type="button"
          onClick={() => chooseMode('UNKNOWN')}
        >
          <HelpCircle />
          <span>
            <strong>{t.place.unknown}</strong>
          </span>
          <span className="radio-dot" />
        </button>
      </section>
      {mode === 'DIFFERENT' ? (
        <section className="flow-card place-search">
          <Field label={t.place.search}>
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                update({ place: null });
              }}
              placeholder={t.place.freeform}
              autoComplete="off"
            />
          </Field>
          {loading ? <p className="field-hint">Searching...</p> : null}
          {suggestions.length ? (
            <ul className="suggestions">
              {suggestions.map((suggestion) => (
                <li key={suggestion.placeId}>
                  <button type="button" onClick={() => void chooseSuggestion(suggestion)}>
                    <MapPin size={16} />
                    {suggestion.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {searchError ? (
            <Notice tone="warning">
              Search is temporarily unavailable. You can enter the place manually.
            </Notice>
          ) : null}
          <Field label={t.place.context}>
            <Input
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder={t.place.context}
            />
          </Field>
        </section>
      ) : mode === 'VENUE' ? (
        <section className="flow-card">
          <Field label={t.place.context}>
            <Input
              value={context}
              onChange={(event) => setContext(event.target.value)}
              placeholder={t.place.context}
            />
          </Field>
        </section>
      ) : null}
      <div className="flow-actions">
        <Button variant="secondary" onClick={() => navigate('/report/intake')}>
          {t.common.back}
        </Button>
        <Button disabled={!canContinue} onClick={continueFlow}>
          {t.common.continue}
        </Button>
      </div>
    </AppShell>
  );
}
